import React, { useCallback, useEffect, useState } from "react";
import clsx from "clsx";
import PropTypes from "prop-types";
import {
  AppBar,
  Button,
  Container,
  CssBaseline,
  Divider,
  Drawer,
  IconButton,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Popover,
  Toolbar,
  Typography,
} from "@material-ui/core";
import AccountCircle from "@material-ui/icons/AccountCircle";
import ChevronLeftIcon from "@material-ui/icons/ChevronLeft";
import ExitToAppTwoTone from "@material-ui/icons/ExitToAppTwoTone";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import MenuIcon from "@material-ui/icons/Menu";
import Settings from "@material-ui/icons/Settings";
import { Link, useNavigate } from "react-router-dom";

import AuthService from "../services/AuthService";
import ModeList from "./ModeList";
import SMQSidebartreeView from "./SMQSidebartreeView";
import useSMQMainContainerStyles from "./SMQMainContainer.styles";

const MOBILE_TABLET_DRAWER_QUERY = "(max-width: 1024px)";

const isMobileOrTabletDevice = () =>
  typeof window !== "undefined" &&
  typeof window.matchMedia === "function" &&
  window.matchMedia(MOBILE_TABLET_DRAWER_QUERY).matches;

function ListItemLink(props) {
  return <ListItem button component={Link} {...props} />;
}

function SMQMainContainer({
  badgeCounts,
  children,
  onMenuSelect,
  sidebarLinks,
  title,
}) {
  const classes = useSMQMainContainerStyles();
  const navigate = useNavigate();
  const [isMobileOrTablet, setIsMobileOrTablet] = useState(
    isMobileOrTabletDevice
  );
  const [drawerOpen, setDrawerOpen] = useState(
    () => !isMobileOrTabletDevice()
  );
  const [modePopoverAnchor, setModePopoverAnchor] = useState(null);
  const [accountPopoverAnchor, setAccountPopoverAnchor] = useState(null);

  useEffect(() => {
    if (
      typeof window === "undefined" ||
      typeof window.matchMedia !== "function"
    ) {
      return undefined;
    }

    const mediaQuery = window.matchMedia(MOBILE_TABLET_DRAWER_QUERY);
    const handleDeviceChange = (event) => {
      setIsMobileOrTablet(event.matches);
      setDrawerOpen(!event.matches);
    };

    handleDeviceChange(mediaQuery);

    if (typeof mediaQuery.addEventListener === "function") {
      mediaQuery.addEventListener("change", handleDeviceChange);
      return () => mediaQuery.removeEventListener("change", handleDeviceChange);
    }

    mediaQuery.addListener(handleDeviceChange);
    return () => mediaQuery.removeListener(handleDeviceChange);
  }, []);

  const closeDrawerOnMobile = useCallback(() => {
    if (isMobileOrTablet) {
      setDrawerOpen(false);
    }
  }, [isMobileOrTablet]);

  const handleMenuSelect = useCallback(
    (key, item) => {
      if (typeof onMenuSelect === "function") {
        onMenuSelect(key, item);
      }
    },
    [onMenuSelect]
  );

  const handleModeSelect = (selectedMode) => {
    setModePopoverAnchor(null);
    navigate(selectedMode.link);
  };

  const handleLogout = () => {
    setAccountPopoverAnchor(null);
    AuthService.logout();
  };

  const modePopoverOpen = Boolean(modePopoverAnchor);
  const accountPopoverOpen = Boolean(accountPopoverAnchor);
  const modePopoverId = modePopoverOpen ? "smq-mode-popover" : undefined;
  const accountPopoverId = accountPopoverOpen
    ? "smq-account-popover"
    : undefined;

  return (
    <div className={classes.root}>
      <CssBaseline />

      <AppBar
        className={clsx(classes.appBar, {
          [classes.appBarShift]: drawerOpen && !isMobileOrTablet,
        })}
        position="fixed"
      >
        <Toolbar className={classes.toolbar}>
          <IconButton
            aria-label="Ouvrir le menu SMQ"
            className={clsx(classes.menuButton, {
              [classes.menuButtonHidden]: drawerOpen && !isMobileOrTablet,
            })}
            color="inherit"
            edge="start"
            onClick={() => setDrawerOpen(true)}
          >
            <MenuIcon />
          </IconButton>

          <Typography
            className={classes.title}
            component="h1"
            noWrap
            variant="h6"
          >
            {title}
          </Typography>

          <div className={classes.expand} />

          <div className={classes.modeSwitcher}>
            <Button
              aria-controls={modePopoverId}
              aria-haspopup="true"
              color="inherit"
              endIcon={<ExpandMoreIcon />}
              onClick={(event) => setModePopoverAnchor(event.currentTarget)}
            >
              Non-conformité / SMQ
            </Button>
          </div>

          <Popover
            anchorEl={modePopoverAnchor}
            anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
            id={modePopoverId}
            onClose={() => setModePopoverAnchor(null)}
            open={modePopoverOpen}
            transformOrigin={{ vertical: "top", horizontal: "right" }}
          >
            <ModeList onSelectMode={handleModeSelect} />
          </Popover>

          <IconButton
            aria-controls={accountPopoverId}
            aria-haspopup="true"
            aria-label="Ouvrir le menu utilisateur"
            color="inherit"
            onClick={(event) => setAccountPopoverAnchor(event.currentTarget)}
          >
            <Settings />
          </IconButton>

          <Popover
            anchorEl={accountPopoverAnchor}
            anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
            id={accountPopoverId}
            onClose={() => setAccountPopoverAnchor(null)}
            open={accountPopoverOpen}
            transformOrigin={{ vertical: "top", horizontal: "right" }}
          >
            <List component="nav" aria-label="Menu utilisateur">
              <ListItemLink
                onClick={() => setAccountPopoverAnchor(null)}
                to="/myAccount"
              >
                <ListItemIcon>
                  <AccountCircle />
                </ListItemIcon>
                <ListItemText primary="Mon compte" />
              </ListItemLink>
              <ListItemLink onClick={handleLogout} to="/">
                <ListItemIcon>
                  <ExitToAppTwoTone />
                </ListItemIcon>
                <ListItemText primary="Déconnexion" />
              </ListItemLink>
            </List>
          </Popover>
        </Toolbar>
      </AppBar>

      <Drawer
        classes={{
          paper: clsx(classes.drawerPaper, {
            [classes.drawerPaperClose]: !isMobileOrTablet && !drawerOpen,
          }),
        }}
        ModalProps={{ keepMounted: true }}
        onClose={() => setDrawerOpen(false)}
        open={drawerOpen}
        variant={isMobileOrTablet ? "temporary" : "permanent"}
      >
        <div className={classes.drawerHeader}>
          <Link
            aria-label="Retour à l'accueil"
            className={classes.logo}
            to="/home"
          />
          <IconButton
            aria-label="Fermer le menu SMQ"
            onClick={() => setDrawerOpen(false)}
          >
            <ChevronLeftIcon />
          </IconButton>
        </div>
        <Divider />
        <SMQSidebartreeView
          badgeCounts={badgeCounts}
          links={sidebarLinks}
          onNavigate={closeDrawerOnMobile}
          onSelect={handleMenuSelect}
        />
      </Drawer>

      <main className={classes.content}>
        <div className={classes.appBarSpacer} />
        <Container
          className={classes.container}
          disableGutters
          maxWidth={false}
        >
          {children}
        </Container>
      </main>
    </div>
  );
}

SMQMainContainer.defaultProps = {
  badgeCounts: {},
  children: null,
  onMenuSelect: undefined,
  sidebarLinks: {},
  title: "Non-conformité / SMQ",
};

SMQMainContainer.propTypes = {
  badgeCounts: PropTypes.objectOf(
    PropTypes.oneOfType([PropTypes.number, PropTypes.string])
  ),
  children: PropTypes.node,
  onMenuSelect: PropTypes.func,
  sidebarLinks: PropTypes.objectOf(PropTypes.string),
  title: PropTypes.string,
};

export default SMQMainContainer;
